// <s-countdown> — live countdown to [data-end] (an ISO date string).
// Updates [data-days], [data-hours], [data-minutes], [data-seconds] once per second and
// hides the element (and its [data-countdown-wrapper] ancestor, when present) after the end time.
// Optional loop mode: [data-loop] (seconds) and [data-loop-reset] (seconds). The timer counts down
// from data-loop, and when data-loop-reset seconds remain it restarts from data-loop. Cycles are
// anchored to a fixed epoch so every visitor sees the same time. It still hides at [data-end].
// One component per file.
class SCountdown extends HTMLElement {
  #timer = null;
  #end = NaN;
  #loop = 0;
  #reset = 0;

  connectedCallback() {
    this.#end = Date.parse(this.dataset.end || "");
    if (Number.isNaN(this.#end)) return;
    this.#loop = Math.max(0, parseInt(this.dataset.loop || "0", 10) || 0);
    this.#reset = Math.max(0, parseInt(this.dataset.loopReset || "0", 10) || 0);
    if (this.#reset >= this.#loop) this.#reset = 0;
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #remaining() {
    const now = Date.now();
    const toEnd = Math.max(0, Math.floor((this.#end - now) / 1000));
    if (!this.#loop || toEnd === 0) return toEnd;
    const cycle = this.#loop - this.#reset;
    const elapsed = Math.floor(now / 1000) % cycle;
    return Math.min(toEnd, this.#loop - elapsed);
  }

  #tick() {
    const remaining = this.#remaining();
    if (remaining === 0) {
      window.clearInterval(this.#timer);
      const target = this.closest("[data-countdown-wrapper]") || this;
      target.hidden = true;
      return;
    }
    const hasDays = Boolean(this.querySelector("[data-days]"));
    const parts = {
      days: Math.floor(remaining / 86400),
      hours: hasDays ? Math.floor((remaining % 86400) / 3600) : Math.floor(remaining / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    };
    for (const [unit, value] of Object.entries(parts)) {
      const el = this.querySelector(`[data-${unit}]`);
      if (el) el.textContent = String(value).padStart(2, "0");
    }
  }
}

if (!customElements.get("s-countdown")) customElements.define("s-countdown", SCountdown);
